// REST + WS clients. Auth is handled by the backend's dev-mode bypass
// when WXRELAY_ENV=dev; we don't ship API keys in the bundle. When a
// real login flow lands, the principal will come from a session
// cookie set by /api/login (out of scope for v1).

export interface TimePair {
  iso: string;     // ET ISO with offset, e.g. "2026-04-27T14:32:05.123-04:00"
  utc: string;     // UTC ISO, e.g. "2026-04-27T18:32:05.123+00:00"
}

export interface ReadingPayload {
  station_id: number;
  source: string;
  observed_at: TimePair;
  source_fetched_at: TimePair;
  received_at: TimePair;
  temp_f: number | null;
  dewpoint_f: number | null;
  wind_speed_kt: number | null;
  wind_dir_deg: number | null;
  wind_gust_kt: number | null;
  pressure_in: number | null;
  visibility_mi: number | null;
  // Synoptic 6h/24h extremes from METAR remarks. 6h fields are
  // backend-filtered to be null unless the synoptic's 6h window is
  // fully within today's climate day.
  temp_6h_max_f?: number | null;
  temp_6h_min_f?: number | null;
  temp_24h_max_f?: number | null;
  temp_24h_min_f?: number | null;
}

export interface CliPayload {
  station_id: number;
  source: string;
  report_for_date: string;     // YYYY-MM-DD
  high_f: number | null;
  low_f: number | null;
  precip_in: number | null;
  snow_in: number | null;
  raw_url: string;
  source_announced_at: TimePair;
  received_at: TimePair;
}

export interface RtpPayload {
  station_id: number;
  source: string;
  issued_at: TimePair;
  local_date: string;          // YYYY-MM-DD
  today_low_f: number | null;
  yesterday_high_f: number | null;
  precip_today_in: number | null;
  raw_url: string;
  received_at: TimePair;
}

export interface TodayObserved {
  local_date: string;          // YYYY-MM-DD in station's tz
  tz: string;                  // IANA tz name
  high_f: number | null;
  high_source: string | null;  // "metar" | "synoptic_6h" | "madis_hf" | null
  high_at_utc: string | null;  // when the winning high was observed
  low_f: number | null;
  low_source: string | null;
  low_at_utc: string | null;
  // Synoptic-confirmed running extremes (0.1°F-precise, lag up to 6h).
  // Shown alongside the running figure so a trader can see both the
  // fresh-but-fuzzy and precise-but-stale views.
  confirmed_high_f: number | null;
  confirmed_high_at_utc: string | null;
  confirmed_low_f: number | null;
  confirmed_low_at_utc: string | null;
  latest_synoptic_at_utc: string | null;
  n_obs: number;
  latest_obs_at_utc: string | null;
}

export interface YesterdayOfficial {
  weather_day: string | null;       // YYYY-MM-DD (LST climate day)
  high_f: number | null;
  low_f: number | null;
  observed_at_utc: string | null;
}

export interface StationCard {
  id: number;
  icao: string;
  name: string | null;
  lat: number | null;
  lon: number | null;
  sources: string[];
  active: boolean;
  tz: string;
  latest_reading: ReadingPayload | null;
  cli_today: CliPayload | null;       // preliminary running snapshot for today
  cli_yesterday: CliPayload | null;   // settled prior day, canonical settlement reference
  latest_rtp: RtpPayload | null;
  today_observed: TodayObserved;
  yesterday_official: YesterdayOfficial | null;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/api${path}`);
  if (!res.ok) throw new Error(`GET ${path} -> ${res.status}`);
  return res.json() as Promise<T>;
}

export const api = {
  me: () => get<{ name: string; kind: string; scopes: string[] }>("/me"),
  stations: () => get<StationCard[]>("/stations"),
  station: (id: number) => get<StationCard>(`/stations/${id}`),
  readings: (station: number, hours = 24) =>
    get<ReadingPayload[]>(
      `/readings?station=${station}&since=${new Date(
        Date.now() - hours * 3600 * 1000
      ).toISOString()}`
    ),
  cliReports: (station: number, days = 30) =>
    get<CliPayload[]>(`/cli_reports?station=${station}&days=${days}`),
};

// --- WebSocket ---

export type WSEvent =
  | { type: "reading"; data: ReadingPayload & { broadcast_at: TimePair; age_seconds: number } }
  | { type: "cli"; data: CliPayload & { broadcast_at: TimePair } }
  | { type: "rtp"; data: RtpPayload & { broadcast_at: TimePair } }
  | { type: "heartbeat"; broadcast_at: TimePair };

export interface WSConnection {
  close(): void;
}

export function openWS(onEvent: (ev: WSEvent) => void, onStatus?: (s: "open" | "closed") => void): WSConnection {
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  const url = `${proto}://${window.location.host}/ws`;
  let ws: WebSocket;
  let stopped = false;
  let backoffMs = 500;

  function connect() {
    ws = new WebSocket(url);
    ws.onopen = () => { backoffMs = 500; onStatus?.("open"); };
    ws.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data) as WSEvent;
        onEvent(ev);
      } catch { /* ignore */ }
    };
    ws.onclose = () => {
      onStatus?.("closed");
      if (stopped) return;
      backoffMs = Math.min(backoffMs * 2, 15000);
      setTimeout(connect, backoffMs);
    };
    ws.onerror = () => ws.close();
  }
  connect();

  return {
    close() {
      stopped = true;
      ws?.close();
    },
  };
}
